import clsx from 'clsx'
import { useState } from 'react'

import Button from '../../components/Button'
import LoadingText from '../../components/LoadingText'
import type { LoadableComponentProps } from '../../types'

type BenefitCardProps = {
  title: string
  benefitCode: string
  state: 'active' | 'inactive' | 'used' | 'activeAutomatically'
  labels: {
    active: { status: string; button: string }
    inactive: { status: string; button: string }
    used: { status: string }
    activeAutomatically: { status: string }
  }
  onActivate: (benefitCode: string) => Promise<void>
  onDeactivate: (benefitCode: string) => Promise<void>
  className?: string
}

const BenefitCard = ({
  data,
  loading,
}: LoadableComponentProps<BenefitCardProps> & { className?: string }) => {
  const {
    title,
    benefitCode = '',
    state = 'inactive',
    labels,
    onActivate,
    onDeactivate,
    className,
  } = data || {}
  const [buttonLoading, setButtonLoading] = useState(false)

  return (
    <div
      data-testid='benefit-card'
      data-benefit-code={benefitCode}
      data-benefit-state={state}
      className={clsx(
        'radius-md flex max-w-[220px] flex-col items-center bg-ecruwhite lg:rounded-md',
        state === 'used' && 'opacity-50',
        className,
      )}
    >
      <p
        className={clsx(
          'sm w-full rounded-t-sm p-1 pl-4 lg:rounded-t-md',
          (state === 'active' || state === 'activeAutomatically') &&
            'border-b border-green bg-green/10',
          state === 'inactive' && 'border-b border-darkgray/30 bg-steelblue',
          state === 'used' && 'bg-steelblue',
        )}
      >
        {!loading ? labels?.[state].status : <LoadingText className='inline' textLength={10} />}
      </p>
      <div
        className={clsx(
          'flex h-full min-h-[180px] w-full flex-col justify-between rounded-b-sm px-2 py-4 md:px-4 lg:rounded-b-md',
          state === 'used' && 'bg-steelblue/30',
          state === 'inactive' && 'bg-steelblue',
        )}
      >
        <p className='lg break-words pt-4 text-center font-semibold'>
          {!loading ? title : <LoadingText className='inline' textLength={10} />}
        </p>
        {(state === 'active' || state === 'inactive') && (
          <div className='mt-10 w-full border-t border-dashed border-white pt-6'>
            <Button
              isLoading={loading}
              className={clsx(
                'justify-self-end !p-2',
                state === 'active' && '!w-fit text-darkred !outline-darkred',
                state === 'inactive' && '!max-w-none',
                buttonLoading && 'pointer-events-none opacity-30',
              )}
              appearance='transparent'
              onPress={async () => {
                setButtonLoading(true)
                if (state === 'active') {
                  await onDeactivate?.(benefitCode)
                } else {
                  await onActivate?.(benefitCode)
                }
                setButtonLoading(false)
              }}
            >
              {state === 'active' ? labels?.active?.button : labels?.inactive?.button}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default BenefitCard
